// This API queries the proxy middleware in ASP to directly get Drupal data
// The generated types allow parsing of the responses,
// which will then be transformed to match the requirements of the components
import { Map, OrderedMap, Set } from 'immutable';
import * as Moment from 'moment';
import { node_parser } from '../../Shared/api/drupal/parser';
import * as QueryContext from '../custom_components/odata_context';
import { moment } from '../custom_utils/moment';
import { defaultGetOptions, handle } from './api_utils';
import * as ConvertNodeTo from './drupal_node_conversion';
const odataBaseUrl = `/api/v1/OData/GetQuery?Query=`;
export const loadingText = "Bezig met laden...";
export const errorText = "De data kon niet worden geladen.";
export const noResultsText = "Er zijn geen resultaten gevonden.";
const drupalUrl = "/api/drupal";
const catchError = ()=>(e)=>{
        console.log(e);
        return "error";
    };
const get = (url)=>{
    return fetch(url).then((x)=>x.status == 200 ? x.json() : Promise.reject(x.statusText)).then((x)=>x).catch(catchError());
};
export const getCollectionPage = async ()=>{
    const res = await fetch(drupalUrl + '/collectie', defaultGetOptions());
    const temp = await handle(res);
    if (temp.kind != 'result') return temp;
    const n = node_parser(temp.value);
    if (n.bundle != "collection_overview") throw new Error("/collection is not a 'collection_overview'");
    const p = ConvertNodeTo.collectionPage(n);
    return {
        ...temp,
        value: p
    };
};
export const getDatesForPeriod = (p)=>{
    let start = Moment().startOf('day');
    let end = Moment().endOf('day');
    start.add(p == 'morgen' ? 1 : 0, 'days');
    end.add(p == 'vandaag' ? 0 : p == 'morgen' ? 1 : p == 'komende-zeven-dagen' ? 7 : p == 'komende-dertig-dagen' ? 30 : 9999, 'days');
    return {
        start,
        end
    };
};
const previewUrl = "/api";
const constructUrl = (route, params)=>{
    const preview = new RegExp("^/node-preview/").test(route);
    if (preview) {
        return `${previewUrl}${route}${params}`;
    }
    return `${drupalUrl}${route}`;
};
export const getPage = (r, p)=>get(constructUrl(r, p)).then((x)=>{
        if (x == "loading" || x == "error") return "error";
        try {
            const n = node_parser(x);
            const p = n.bundle == "activity_clusters" ? ConvertNodeTo.activityClustersPage(n) : n.bundle == "activity_detail" ? ConvertNodeTo.activityDetailPage(n) : n.bundle == "collection_overview" ? ConvertNodeTo.collectionPage(n) : n.bundle == "location_detail" ? ConvertNodeTo.locationDetailPage(n) : n.bundle == "home" ? ConvertNodeTo.homePage(n) : n.bundle == "report_overview" ? ConvertNodeTo.reportOverview(n) : n.bundle == "faq" ? 'error' // This content type is not a page
             : n.bundle == 'blog_overview' ? 'error' : n.bundle == 'blog_post' ? 'error' : n.bundle == 'location' ? 'error' : n.bundle == 'location_overview' ? 'error' : n.bundle == 'news_item' ? 'error' : n.bundle == 'news_overview' ? 'error' : n.bundle == 'newsletter' ? 'error' : n.bundle == 'event' // Events will be handled by the Events feature
             ? 'error' : ConvertNodeTo.generalContentPage(n);
            return p;
        } catch (e) {
            console.log(e);
            console.log("Are you sure all the required fields in Drupal are filled?");
        }
        return 'error';
    });
export const getRegions = ()=>{
    let regions = QueryContext.RegionQuery(odataBaseUrl).Select("Id", "Title", 'Slug').IncludeStrict("Region_Location", (q)=>q.Select("RegionId", "LocationId")).ToList();
    return regions.then((r)=>{
        try {
            return r.reduce((a, e)=>a.set(e.Title, {
                    id: e.Id,
                    slug: e.Slug,
                    name: e.Title,
                    locations: e.Region_Location.map((l)=>l.LocationId).toList()
                }), Map()).sort((a, b)=>a.slug.localeCompare(b.slug)).toOrderedMap();
        } catch (e) {
            console.error(e);
            return "error";
        }
    });
};
const filterBy = (filter, ids)=>{
    const result = filter.ActivityEventId.Equals(ids.first());
    return ids.size == 1 ? result : result.Or(filterBy(filter, ids.rest().toList()));
};
export const getEventFilter = async ()=>{
    const filterResult = await get('/api/v1/Event/GetEventFilters');
    if (filterResult == 'error' || filterResult == 'loading') return;
    return {
        categories: OrderedMap(filterResult.categories.map((c)=>[
                c.id,
                c.name
            ])),
        regions: OrderedMap(filterResult.regions.map((c)=>[
                c.id,
                {
                    name: c.name,
                    slug: c.slug
                }
            ]))
    };
};
export const getEvents = async (options)=>{
    const { selectedRegion, selectedCategories, page, pageSize, from, until, search } = options;
    const params = [
        `page=${page}`,
        `pageSize=${pageSize}`
    ];
    if (selectedRegion) {
        params.push(`regions=${selectedRegion}`);
    }
    if (from) {
        params.push(`from=${Moment(from).format("YYYY-MM-DD")}`);
    }
    if (until) {
        params.push(`until=${Moment(until).format("YYYY-MM-DD")}`);
    }
    if (search.trim()) {
        params.push(`query=${encodeURIComponent(search.trim())}`);
    }
    if (!selectedCategories.isEmpty()) {
        selectedCategories.forEach((categorie)=>{
            params.push(`categories=${categorie}`);
        });
    }
    const query = params.length == 0 ? '' : '?' + params.join('&');
    const eventResult = await get('/api/v1/Event/GetEvents' + query);
    if (eventResult == 'error' || eventResult == 'loading') return;
    const convertedResult = {
        ...eventResult,
        events: eventResult.events.map((e)=>({
                ...e,
                date: new Date(e.date)
            })),
        categoryEventCount: Map(eventResult.categoryEventCount),
        regionEventCount: Map(eventResult.regionEventCount),
        anyRegionEventCount: eventResult.anyRegionEventCount,
        datesEventCount: eventResult.datesEventCount
    };
    return convertedResult;
};
export const getNavigation = (id)=>get(`${drupalUrl}/api/menu_items/${id}`);
export const getDrupalActivities = (pdbIds)=>get(`${drupalUrl}/api/activities/${pdbIds.join("+")}`);
export const getDrupalVacancies = (categoryIds)=>get(`${drupalUrl}/api/vacancies/${categoryIds.join("+")}`);
export const getDrupalLocations = (pdbIds)=>get(`${drupalUrl}/api/locations/${pdbIds.join("+")}`);
export const getAllDrupalLocations = ()=>get(`${drupalUrl}/api/locations/all`);
export const getDrupalReports = ()=>get(`${drupalUrl}/api/reports`);
export const getReports = ()=>new Promise(async (res)=>{
        let drupalReports = await getDrupalReports();
        let reports = Map();
        if (drupalReports != 'error' && drupalReports != 'loading') {
            drupalReports.forEach((r)=>{
                if (!reports.has(r.id)) {
                    reports = reports.set(r.id, {
                        created: r.created,
                        datetimes: Set(),
                        imageAlt: r.imageAlt,
                        imageUrl: r.imageUrl,
                        imageUrlFallback: r.imageUrlFallback,
                        locations: Set(),
                        shortDescription: r.shortDescription,
                        title: r.title,
                        url: r.url
                    });
                }
                reports = reports.set(r.id, {
                    ...reports.get(r.id),
                    datetimes: r.datetime != "" ? reports.get(r.id).datetimes.add(moment.unix(parseInt(r.datetime))) : reports.get(r.id).datetimes,
                    locations: r.location != "" ? reports.get(r.id).locations.add(r.location) : reports.get(r.id).locations
                });
            });
            reports = reports.sort((a, b)=>{
                const dateA = a.datetimes.first();
                const dateB = b.datetimes.first();
                if (dateA == undefined || dateB == undefined) return 0;
                return dateB.diff(dateA);
            }).toOrderedMap();
            res(reports);
        } else {
            res('error');
        }
    });
export const getTeasers = (ids)=>new Promise(async (res)=>{
        try {
            let teasers = await get(`${drupalUrl}/api/teasers/${ids.join("+")}`);
            if (teasers == 'error' || teasers == 'loading') throw new Error('Could not load teasers');
            res(OrderedMap(teasers.map((t)=>[
                    t.id,
                    t
                ])).sort((a, b)=>ids.indexOf(a.id) - ids.indexOf(b.id)).toOrderedMap());
        } catch (e) {
            console.error(e);
            res('error');
        }
    });
